import React from 'react';
import { Calendar, ChevronRight, MapPin, Navigation, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/routes/paths';
import type { UpcomingVisitItem } from './types';

interface UpcomingVisitsWidgetProps {
  visits: UpcomingVisitItem[];
}

export const UpcomingVisitsWidget: React.FC<UpcomingVisitsWidgetProps> = ({ visits }) => {
  const navigate = useNavigate();

  const getPurposeBadge = (type: string) => {
    switch (type) {
      case 'followup':
        return 'bg-primary-container text-primary border-primary/30';
      case 'product_demo':
        return 'bg-success/10 text-success-text border-success/30';
      case 'initial_meeting':
        return 'bg-info/10 text-info-text border-info/30';
      case 'quotation_discussion':
        return 'bg-primary-container text-primary-dim border-primary/30';
      default:
        return 'bg-surface-variant text-on-surface-variant border-border';
    }
  };

  return (
    <div className="flex h-full flex-col justify-between rounded-card border border-border bg-card p-5 shadow-xs">
      {/* Header */}
      <div>
        <div className="flex items-center justify-between border-b border-outline/80 pb-3">
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary-container text-primary">
              <Calendar className="h-4 w-4" />
            </div>
            <div>
              <span className="text-body-sm font-semibold uppercase tracking-wider text-on-surface">
                Upcoming Visits
              </span>
              <div className="text-data-xs text-on-surface-subtle">Scheduled on-site client interactions</div>
            </div>
          </div>

          <button
            type="button"
            onClick={() => navigate(ROUTES.visits)}
            className="inline-flex items-center gap-1 text-data-xs font-semibold text-primary hover:underline"
          >
            <span>View All</span>
            <ChevronRight className="h-3 w-3" />
          </button>
        </div>

        {/* Visits List */}
        {visits.length === 0 ? (
          <div className="my-6 text-center text-body-sm text-on-surface-subtle py-4">
            No upcoming field visits scheduled.
          </div>
        ) : (
          <div className="my-2 flex flex-col divide-y divide-border">
            {visits.map((v) => (
              <div
                key={v.id}
                className="flex items-center justify-between py-2.5 transition hover:bg-surface-variant px-2 rounded-lg"
              >
                <div className="flex items-start gap-3">
                  <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-surface-container text-on-surface-variant mt-0.5 shrink-0">
                    <Clock className="h-3.5 w-3.5" />
                  </div>

                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-body-sm text-on-surface">{v.dateTime}</span>
                      <span className="text-on-surface-subtle">•</span>
                      <span className="font-semibold text-body-sm text-on-surface">{v.client}</span>
                    </div>

                    <div className="flex items-center gap-1 text-data-xs text-on-surface-subtle mt-0.5">
                      <MapPin className="h-3 w-3 text-on-surface-subtle" />
                      <span>{v.location}</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <span
                    className={`rounded-md border px-2 py-0.5 text-data-xs font-semibold capitalize ${getPurposeBadge(
                      v.purposeType
                    )}`}
                  >
                    {v.purpose}
                  </span>

                  <a
                    href={`https://maps.google.com/?q=${encodeURIComponent(v.location)}`}
                    target="_blank"
                    rel="noreferrer"
                    title="Open GPS Navigation"
                    className="flex h-7 w-7 items-center justify-center rounded-lg border border-border text-on-surface-variant hover:bg-info/10 hover:text-info-text hover:border-info/30 transition"
                  >
                    <Navigation className="h-3.5 w-3.5" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="rounded-lg border border-border bg-surface-variant p-3 flex items-center justify-between text-body-sm">
        <span className="text-body-sm text-on-surface-variant">Upcoming visits</span>
        <span className="font-mono text-data-md font-semibold tabular-nums text-on-surface">{visits.length}</span>
      </div>
    </div>
  );
};
